import { Dispatch, SetStateAction } from "react"
import { ChartData, ChartDataset, ChartOptions, ChartType, Color, Tooltip, TooltipPositionerFunction } from "chart.js"
import i18n from "@/i18n"
import { getSlotLabel, SLOTS_PER_DAY } from "@/lib/slot"
import { chartColors } from "@/modules/productivity-cockpit/helpers/planning-chart-config"
import { UpdateCapacity } from "@/modules/productivity-cockpit/planning-chart"
import { CapacityPlan } from "@/modules/productivity-cockpit/services/useCapacityPlansQuery"

declare module "chart.js" {
  interface TooltipPositionerMap {
    myCustomPositioner: TooltipPositionerFunction<ChartType>
  }
}

Tooltip.positioners.myCustomPositioner = function (_, eventPosition) {
  return {
    x: eventPosition.x,
    y: eventPosition.y,
  }
}

export const PlanningBarIndex = {
  BOOKED_CAPACITY: 1,
  AVAILABLE_CAPACITY: 2,
  BOOKED_EMERGENCY_CAPACITY: 3,
  AVAILABLE_EMERGENCY_CAPACITY: 4,
} as const

const sum = (arr: unknown[]): number => arr.reduce((acc: number, val) => acc + (typeof val === "number" ? val : 0), 0)

const isBarUpdatable = (index: number) =>
  PlanningBarIndex.AVAILABLE_CAPACITY === index || PlanningBarIndex.AVAILABLE_EMERGENCY_CAPACITY === index

const getMaxValueFromChartData = (chartData: ChartData): number => {
  const arrayOfMaxValues = Array.from(Array(SLOTS_PER_DAY).keys()).map((slotId) => {
    // the only line we have for now is the forecast, this implementation should be smarter as soon as we display differently
    const rawForecastValue = chartData.datasets.find((ds) => ds.type === "line")?.data[slotId]?.valueOf()
    let forecastValue = -999999
    if (typeof rawForecastValue === "number") {
      forecastValue = rawForecastValue
    }

    const barSumValue = sum(
      chartData.datasets
        .filter((ds) => ds.type !== "line")
        .map((ds) => ds.data[slotId]?.valueOf())
        .filter((data) => typeof data === "number"),
    )

    return Math.max(
      typeof forecastValue === "number" ? forecastValue : 0,
      typeof barSumValue === "number" ? barSumValue : 0,
    )
  })

  return Math.ceil(Math.max(...arrayOfMaxValues))
}

const generatePlanningChartOptions: (
  chartData: ChartData,
  setUpdateCapacity: Dispatch<SetStateAction<UpdateCapacity | null>>,
  isToday: boolean,
  currentSLotId: number | undefined,
) => ChartOptions = (
  chartData: ChartData,
  setUpdateCapacity: Dispatch<SetStateAction<UpdateCapacity | null>>,
  isToday: boolean,
  currentSLotId: number | undefined,
) => {
  const maxValue = chartData ? getMaxValueFromChartData(chartData) : 0
  const { t } = i18n

  return {
    plugins: {
      title: {
        display: true,
        position: "bottom",
        text: t("scheduling.productivityCockpit.planningChart.title"),
        font: {
          size: 14,
          weight: "bold",
        },
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        caretSize: 0,
        cornerRadius: 4,
        callbacks: {
          label: (context) => context.dataset.label + ": " + context.parsed.y,
          title: () => "",
        },
        backgroundColor: "#2C3037",
        position: "myCustomPositioner",
        animation: { easing: "easeOutCubic" },
      },
    },
    responsive: true,
    scales: {
      x: {
        stacked: true,
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          minRotation: -60,
          callback: function (value, index) {
            const slot = this.getLabelForValue(Number(value))
            return index % 2 === 0 ? slot : ""
          },
          color: "#57606E",
          font: (ctx) => {
            const currentSlotLabel = currentSLotId && getSlotLabel(currentSLotId)
            if (ctx.tick.label === currentSlotLabel && isToday) {
              return {
                size: 12,
                weight: "bolder",
              }
            }
            return {
              size: 12,
              weight: "normal",
            }
          },
        },
      },
      y: {
        stacked: true,
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        suggestedMax: maxValue,
        ticks: {
          stepSize: maxValue > 10 ? 10 : 2,
        },
      },
    },
    animation: {
      duration: 0,
    },
    onClick: (event, elements) => {
      if (elements.length > 0 && event.x && event.y) {
        const { datasetIndex, index } = elements[elements.length <= 1 ? 0 : 1]
        if (isBarUpdatable(datasetIndex)) {
          setUpdateCapacity({
            index,
            datasetIndex,
            clientX: event.x,
            clientY: event.y,
          })
        } else setUpdateCapacity(null)
      } else setUpdateCapacity(null)
    },
  }
}

const generatePlanningChartData = (
  slotCapacityPlans: (CapacityPlan | null)[],
  updateCapacity: UpdateCapacity | null,
  isToday: boolean,
): ChartData => {
  const { t } = i18n
  const { index, datasetIndex } = updateCapacity || {}

  const borderWidth: number[] = []
  const availableCapacityData: number[] = []
  const availableEmergencyCapacityData: number[] = []
  const bookedEmergencyCapacityData: number[] = []
  const bookedCapacityData: number[] = []
  const forecastedCapacityData: number[] = []

  const availableCapacityBg: (Color | undefined)[] = []
  const availableEmergencyCapacityBg: (Color | undefined)[] = []

  slotCapacityPlans.forEach((slotData, slotIndex) => {
    borderWidth.push(slotData?.isCurrentSlot && isToday ? 2 : 0)
    availableCapacityData.push(!slotData ? 0 : slotData.availablePoolCapacity)
    availableEmergencyCapacityData.push(!slotData ? 0 : slotData.availableEmergencyCapacity)
    bookedEmergencyCapacityData.push(slotData?.bookedEmergencyCapacity ?? 0)
    bookedCapacityData.push(slotData?.bookedPoolCapacity ?? 0)
    forecastedCapacityData.push(slotData?.forecastedCapacity ?? 0)

    availableCapacityBg.push(
      PlanningBarIndex.AVAILABLE_CAPACITY === datasetIndex && index === slotIndex
        ? chartColors.get("availableSelected")
        : chartColors.get("available"),
    )
    availableEmergencyCapacityBg.push(
      PlanningBarIndex.AVAILABLE_EMERGENCY_CAPACITY === datasetIndex && index === slotIndex
        ? chartColors.get("availableReservedSelected")
        : chartColors.get("availableReserved"),
    )
  })

  const availableCapacity: ChartDataset = {
    ...barChartBase,
    label: t("scheduling.productivityCockpit.planningChart.available"),
    data: availableCapacityData,
    backgroundColor: availableCapacityBg,
    borderWidth,
    hoverBackgroundColor: chartColors.get("availableHover"),
  }

  const availableEmergencyCapacity: ChartDataset = {
    ...barChartBase,
    label: t("scheduling.productivityCockpit.planningChart.availableEmergency"),
    data: availableEmergencyCapacityData,
    backgroundColor: availableEmergencyCapacityBg,
    borderWidth,
    hoverBackgroundColor: chartColors.get("availableReservedHover"),
  }

  const bookedCapacity: ChartDataset = {
    ...barChartBase,
    label: t("scheduling.productivityCockpit.planningChart.booked"),
    data: bookedCapacityData,
    backgroundColor: chartColors.get("booked"),
    borderWidth,
    hoverBackgroundColor: chartColors.get("bookedHover"),
  }

  const bookedEmergencyCapacity: ChartDataset = {
    ...barChartBase,
    label: t("scheduling.productivityCockpit.planningChart.bookedEmergency"),
    data: bookedEmergencyCapacityData,
    backgroundColor: chartColors.get("bookedEmergency"),
    borderWidth,
    hoverBackgroundColor: chartColors.get("bookedEmergencyHover"),
  }

  const forecastedCapacity: ChartDataset = {
    ...lineChartBase,
    label: t("scheduling.productivityCockpit.planningChart.forecast"),
    borderColor: "#8F5100",
    data: forecastedCapacityData,
  }

  return {
    labels: Array.from({ length: SLOTS_PER_DAY }, (_, i) => getSlotLabel(i)),
    datasets: [
      forecastedCapacity,
      bookedCapacity,
      availableCapacity,
      bookedEmergencyCapacity,
      availableEmergencyCapacity,
    ],
  }
}

const barChartBase: ChartDataset = {
  type: "bar",
  barPercentage: 0.8,
  categoryPercentage: 1,
  borderSkipped: "middle",
  data: [],
  borderColor: "#BA1A1A",
}

const lineChartBase: ChartDataset = {
  type: "line",
  pointRadius: 0,
  stepped: "middle",
  pointHoverRadius: 10,
  pointHitRadius: 10,
  data: [],
}

export {
  generatePlanningChartOptions,
  generatePlanningChartData,
  barChartBase,
  lineChartBase,
  Tooltip as PlanningChartTooltip,
}
